// FaceEmotionDetection Panel Component
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { translate } from '../../../base/i18n/functions';
import { getLocalVideoTrack } from '../../../base/tracks/functions';
import { setFaceEmotionDetectionVisible, setFaceEmotionDetectionEnabled, setFaceEmotionDetectionDetecting, setCurrentFaceData, setFaceEmotionDetectionError } from '../../actions';
import { initializeFaceLandmarker, detectFaceData } from '../../utils/mediapipe';
import './face_emotion_detection-panel.css';
/**
 * 表情検出パネルコンポーネント
 */
const FaceEmotionDetectionPanel = ({ _visible, _enabled, _detecting, _currentFaceData, _error, _localVideoTrack, t, dispatch }) => {
    const videoRef = useRef(null);
    const detectionIntervalRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    // MediaPipe初期化
    useEffect(() => {
        if (_visible && !isInitialized) {
            initializeMediaPipe();
        }
        return () => {
            if (detectionIntervalRef.current) {
                clearInterval(detectionIntervalRef.current);
            }
        };
    }, [_visible]);
    // ビデオストリーム設定
    useEffect(() => {
        if (_localVideoTrack && videoRef.current) {
            // Jitsi track からストリームを取得
            let stream;
            if (_localVideoTrack.jitsiTrack && _localVideoTrack.jitsiTrack.getOriginalStream) {
                stream = _localVideoTrack.jitsiTrack.getOriginalStream();
            }
            else if (_localVideoTrack.jitsiTrack && _localVideoTrack.jitsiTrack.stream) {
                stream = _localVideoTrack.jitsiTrack.stream;
            }
            else if (_localVideoTrack.stream) {
                stream = _localVideoTrack.stream;
            }
            else if (_localVideoTrack.track) {
                stream = new MediaStream([_localVideoTrack.track]);
            }
            if (stream) {
                videoRef.current.srcObject = stream;
                // ビデオの読み込み完了を待つ
                videoRef.current.onloadedmetadata = () => {
                    // ビデオを再生開始
                    videoRef.current?.play().catch(e => {
                        console.error('Failed to play video:', e);
                    });
                };
                videoRef.current.onerror = (e) => {
                    console.error('Video error:', e);
                };
            }
        }
    }, [_localVideoTrack]);
    // 検出開始/停止
    useEffect(() => {
        if (_enabled && _detecting && videoRef.current) {
            startDetection();
        }
        else {
            stopDetection();
        }
        return () => stopDetection();
    }, [_enabled, _detecting]);
    const initializeMediaPipe = async () => {
        try {
            await initializeFaceLandmarker();
            setIsInitialized(true);
        }
        catch (error) {
            console.error('Failed to initialize MediaPipe:', error);
            dispatch(setFaceEmotionDetectionError('MediaPipeの初期化に失敗しました'));
        }
    };
    const startDetection = () => {
        if (!videoRef.current) {
            return;
        }
        detectionIntervalRef.current = setInterval(async () => {
            if (videoRef.current) {
                if (videoRef.current.readyState >= 2 && videoRef.current.videoWidth > 0 && !videoRef.current.paused) {
                    try {
                        const faceData = await detectFaceData(videoRef.current, performance.now());
                        if (faceData) {
                            dispatch(setCurrentFaceData(faceData));
                        }
                    }
                    catch (error) {
                        console.error('Detection error:', error);
                    }
                }
            }
        }, 500); // 500ms間隔で検出
    };
    const stopDetection = () => {
        if (detectionIntervalRef.current) {
            clearInterval(detectionIntervalRef.current);
            detectionIntervalRef.current = null;
        }
    };
    const handleClose = () => {
        dispatch(setFaceEmotionDetectionEnabled(false));
        dispatch(setFaceEmotionDetectionVisible(false));
        stopDetection();
    };
    const handleToggleDetection = () => {
        const newEnabled = !_enabled;
        dispatch(setFaceEmotionDetectionEnabled(newEnabled));
        if (newEnabled) {
            dispatch(setFaceEmotionDetectionDetecting(true));
        }
    };
    const getEmotionIcon = (emotion) => {
        const icons = {
            happy: '😊',
            surprised: '😮',
            sad: '😢',
            angry: '😠',
            neutral: '😐'
        };
        return icons[emotion] || '😐';
    };
    const getEmotionColor = (emotion) => {
        const colors = {
            happy: '#4CAF50',
            surprised: '#FF9800',
            sad: '#2196F3',
            angry: '#F44336',
            neutral: '#9E9E9E'
        };
        return colors[emotion] || '#9E9E9E';
    };
    if (!_visible) {
        return null;
    }
    return (React.createElement("div", { className: "face_emotion_detection-panel" },
        React.createElement("div", { className: "face_emotion_detection-panel-header" },
            React.createElement("h2", { className: "face_emotion_detection-panel-title" }, t('face_emotion_detection.title')),
            React.createElement("button", { className: "face_emotion_detection-panel-close", onClick: handleClose, "aria-label": t('dialog.close') }, "\u2715")),
        React.createElement("div", { className: "face_emotion_detection-panel-content" },
            React.createElement("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, style: { display: 'none' } }),
            React.createElement("div", { className: "emotion-controls" },
                React.createElement("button", { className: `detection-toggle ${_enabled ? 'active' : ''}`, onClick: handleToggleDetection }, _enabled ? t('face_emotion_detection.stop') : t('face_emotion_detection.start'))),
            _detecting && (React.createElement("div", { className: "detection-status" },
                React.createElement("span", { className: "status-indicator" }),
                "\u9854\u691C\u77E5\u4E2D...")),
            _currentFaceData && (React.createElement("div", { className: "current-face-data" },
                React.createElement("h3", null, "\u691C\u51FA\u7D50\u679C"),
                _currentFaceData.dominantEmotion && (React.createElement("div", { className: "emotion-display" },
                    React.createElement("div", { className: "emotion-icon" }, getEmotionIcon(_currentFaceData.dominantEmotion)),
                    React.createElement("div", { className: "emotion-name" }, t(`face_emotion_detection.emotions.${_currentFaceData.dominantEmotion}`)))),
                _currentFaceData.emotionScores && (React.createElement("div", { className: "emotion-scores" },
                    React.createElement("h4", null, "\u611F\u60C5\u30B9\u30B3\u30A2"),
                    Object.entries(_currentFaceData.emotionScores).map(([emotion, score]) => (React.createElement("div", { key: emotion, className: "emotion-score" },
                        React.createElement("span", { className: "emotion-label" },
                            getEmotionIcon(emotion),
                            " ",
                            t(`face_emotion_detection.emotions.${emotion}`)),
                        React.createElement("div", { className: "emotion-bar" },
                            React.createElement("div", { className: "emotion-bar-fill", style: {
                                    width: `${score * 100}%`,
                                    backgroundColor: getEmotionColor(emotion)
                                } })),
                        React.createElement("span", { className: "emotion-value" },
                            Math.round(score * 100),
                            "%")))))),
                React.createElement("details", { className: "face-parameters" },
                    React.createElement("summary", null, "\u8A73\u7D30\u30D1\u30E9\u30E1\u30FC\u30BF"),
                    React.createElement("div", { className: "parameter-grid" },
                        React.createElement("div", null,
                            "\u773C\u306E\u77AC\u304D(\u5DE6): ",
                            _currentFaceData.eyeBlinkLeft.toFixed(2)),
                        React.createElement("div", null,
                            "\u773C\u306E\u77AC\u304D(\u53F3): ",
                            _currentFaceData.eyeBlinkRight.toFixed(2)),
                        React.createElement("div", null,
                            "\u773C\u3092\u898B\u958B\u304F(\u5DE6): ",
                            _currentFaceData.eyeWideLeft.toFixed(2)),
                        React.createElement("div", null,
                            "\u773C\u3092\u898B\u958B\u304F(\u53F3): ",
                            _currentFaceData.eyeWideRight.toFixed(2)),
                        React.createElement("div", null,
                            "\u53E3\u306E\u958B\u304D: ",
                            _currentFaceData.jawOpen.toFixed(2)),
                        React.createElement("div", null,
                            "\u7B11\u307F: ",
                            _currentFaceData.mouthSmile.toFixed(2)),
                        React.createElement("div", null,
                            "\u53E3\u3092\u3059\u307C\u3081\u308B: ",
                            _currentFaceData.mouthPucker.toFixed(2)),
                        React.createElement("div", null,
                            "\u4E0B\u5507\u3092\u5DFB\u304F: ",
                            _currentFaceData.mouthRollLower.toFixed(2)),
                        React.createElement("div", null,
                            "\u7709\u3092\u4E0A\u3052\u308B: ",
                            _currentFaceData.browInnerUp.toFixed(2)),
                        React.createElement("div", null,
                            "\u7709\u5C3B\u4E0A\u3052\u308B: ",
                            _currentFaceData.browOuterUp.toFixed(2)),
                        React.createElement("div", null,
                            "\u7709\u3092\u4E0B\u3052\u308B: ",
                            _currentFaceData.browDown.toFixed(2)))))),
            _error && (React.createElement("div", { className: "face_emotion_detection-error" },
                React.createElement("p", null,
                    "\u274C ",
                    _error))),
            _enabled && !_currentFaceData && !_error && (React.createElement("div", { className: "no-face-detected" },
                React.createElement("p", null, "\u9854\u304C\u691C\u51FA\u3055\u308C\u3066\u3044\u307E\u305B\u3093"),
                React.createElement("p", null, "\u30AB\u30E1\u30E9\u306B\u9854\u3092\u5411\u3051\u3066\u304F\u3060\u3055\u3044"))))));
};
/**
 * Redux stateをpropsにマッピング
 */
function mapStateToProps(state) {
    const face_emotion_detectionState = state['features/face_emotion_detection'];
    const tracks = state['features/base/tracks'];
    // 正しい方法でローカルビデオトラックを取得
    const localVideoTrack = getLocalVideoTrack(tracks);
    return {
        _visible: face_emotion_detectionState.visible,
        _enabled: face_emotion_detectionState.enabled,
        _detecting: face_emotion_detectionState.detecting,
        _currentFaceData: face_emotion_detectionState.currentFaceData,
        _error: face_emotion_detectionState.error,
        _localVideoTrack: localVideoTrack
    };
}
export default translate(connect(mapStateToProps)(FaceEmotionDetectionPanel));
